import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useVirtualizer,
  useWindowVirtualizer,
  type VirtualizerOptions,
} from "@tanstack/react-virtual";
import { useLocation, useNavigationType } from "src/router";

type PageScroll = {
  /** The window scrolls the page, as beside the classic nav. */
  isWindow: boolean;
  /** The element that scrolls the page in place of the window, once it has mounted. */
  element: HTMLElement | null;
};

/** The window scrolling the page, as beside the classic nav. */
export const WINDOW_PAGE_SCROLL: PageScroll = { isWindow: true, element: null };

const PageScrollContext = createContext<PageScroll>(WINDOW_PAGE_SCROLL);

/** What scrolls the page: the window, or beside the product nav the page card, which scrolls in its place. */
export const usePageScroll = () => useContext(PageScrollContext);

/** The elements scrolling the page in place of the window: the page column, and the card inside it. */
const pageScrollers = new Set<HTMLElement>();

/** How to stop a restore in flight on each scroller, as the viewer's own scrolling does. */
const restoreInputStops = new Map<HTMLElement, () => void>();

/** Makes the element its ref lands on the page's scroller for everything inside it, in place of the window. */
export const usePageScrollRegion = (scope: string) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  usePageScrollRestoration(element, scope);
  useEffect(() => {
    if (!element) return;
    pageScrollers.add(element);
    return () => {
      pageScrollers.delete(element);
    };
  }, [element]);
  const value = useMemo(() => ({ isWindow: false, element }), [element]);
  return { ref: setElement, value };
};

export const PageScrollProvider = PageScrollContext.Provider;

const isVerticalScroller = (element: Element) =>
  element.scrollHeight > element.clientHeight &&
  ["auto", "scroll"].includes(getComputedStyle(element).overflowY);

/** The innermost page scroller with somewhere to go, which is the card when it scrolls and the page column otherwise. */
const getActivePageScroller = () => {
  const scrollers = [...pageScrollers].filter(isVerticalScroller);
  return scrollers.find(
    (scroller) =>
      !scrollers.some(
        (other) => other !== scroller && scroller.contains(other),
      ),
  );
};

/** The line a scroll key moves by, which is about what a browser moves a page by. */
const KEY_SCROLL_LINE_PX = 40;

/** The innermost scroller around `target` that is not a page scroller, short of any page scroller around it. */
const getPaneAround = (target: EventTarget | null) => {
  for (
    let node = target instanceof Element && target.isConnected ? target : null;
    node;
    node = node.parentElement
  ) {
    if (node instanceof HTMLElement && pageScrollers.has(node))
      return undefined;
    if (node instanceof HTMLElement && isVerticalScroller(node)) return node;
  }
  return undefined;
};

/** How far each key that would scroll the window moves the page's scroller. */
const KEY_SCROLL_DELTAS = new Map<
  string,
  (scroller: HTMLElement, event: KeyboardEvent) => number
>([
  ["ArrowDown", () => KEY_SCROLL_LINE_PX],
  ["ArrowUp", () => -KEY_SCROLL_LINE_PX],
  ["PageDown", (scroller) => scroller.clientHeight - KEY_SCROLL_LINE_PX],
  ["PageUp", (scroller) => KEY_SCROLL_LINE_PX - scroller.clientHeight],
  [
    " ",
    (scroller, event) =>
      (event.shiftKey ? -1 : 1) * (scroller.clientHeight - KEY_SCROLL_LINE_PX),
  ],
  ["Home", (scroller) => -scroller.scrollTop],
  ["End", (scroller) => scroller.scrollHeight - scroller.scrollTop],
]);

/**
 * Scrolls the page's scroller with the keyboard while nothing has focus, as the keys would scroll the window.
 * With the window locked, those keys otherwise do nothing until something inside the card takes focus.
 */
export const usePageKeyboardScroll = (isEnabled: boolean) => {
  const { key } = useLocation();
  const entryKey = useRef(key);
  useLayoutEffect(() => {
    entryKey.current = key;
  }, [key]);
  useEffect(() => {
    if (!isEnabled) return;
    let lastPointerTarget: EventTarget | null = null;
    const onPointerDown = (event: PointerEvent) => {
      lastPointerTarget = event.target;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const { target } = event;
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        // The page column takes focus from a click or the skip link, and has nothing of its own to scroll.
        (target !== document.body &&
          target !== document.documentElement &&
          !(target instanceof HTMLElement && pageScrollers.has(target)))
      ) {
        return;
      }
      const getDelta = KEY_SCROLL_DELTAS.get(event.key);
      if (!getDelta) return;
      const pane = getPaneAround(lastPointerTarget);
      if (pane) {
        // With nothing focused, the browser sends the keys to the pane the viewer last clicked into.
        if (
          !(
            target instanceof HTMLElement &&
            pageScrollers.has(target) &&
            target.contains(pane)
          )
        ) {
          return;
        }
        // A click in the page focuses the page column around the pane, which sends the keys there instead.
        event.preventDefault();
        pane.scrollBy({ top: getDelta(pane, event) });
        return;
      }
      // A scroller that mounts after the keys starts where they leave it, not at an offset saved before them.
      for (const storageKey of savedScrollTops.keys()) {
        if (storageKey.endsWith(`:${entryKey.current}`)) {
          savedScrollTops.delete(storageKey);
        }
      }
      // The viewer's keys take over from every restore, even one still waiting for its content to grow tall enough to scroll.
      for (const stop of [...restoreInputStops.values()]) stop();
      const scroller = getActivePageScroller();
      if (!scroller) return;
      event.preventDefault();
      scroller.scrollBy({ top: getDelta(scroller, event) });
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isEnabled]);
};

/** Scrolls the page, the window or the card that scrolls in its place. */
export const scrollPage = (scroll: PageScroll, options: ScrollToOptions) => {
  if (scroll.isWindow) window.scrollTo(options);
  else scroll.element?.scrollTo(options);
};

/** How far the page has scrolled. */
export const getPageScrollTop = (scroll: PageScroll) =>
  scroll.isWindow ? window.scrollY : (scroll.element?.scrollTop ?? 0);

/** The offset of `node`'s top within the page's scrolled content, which a page virtualizer takes as its `scrollMargin`. */
export const getPageScrollMargin = (node: Element, scroll: PageScroll) => {
  const top = node.getBoundingClientRect().top;
  if (scroll.isWindow) return top + window.scrollY;
  if (!scroll.element) return 0;
  return (
    top - scroll.element.getBoundingClientRect().top + scroll.element.scrollTop
  );
};

// Leaves out what depends on the scroller's type, which this hook picks.
type PageVirtualizerOptions<TItemElement extends Element> = Omit<
  VirtualizerOptions<HTMLElement, TItemElement>,
  | "getScrollElement"
  | "observeElementRect"
  | "observeElementOffset"
  | "scrollToFn"
  | "initialOffset"
  | "onChange"
  | "measureElement"
>;

/** A virtualizer driven by whatever scrolls the page, the window or the card in its place; it renders nothing until that has mounted. */
export const usePageVirtualizer = <TItemElement extends Element>(
  options: PageVirtualizerOptions<TItemElement>,
) => {
  const scroll = usePageScroll();
  const { element } = scroll;
  const isEnabled = options.enabled ?? true;
  // One of the two runs at a time; the scroller is fixed for a page's life, so neither switches mid-scroll.
  const windowVirtualizer = useWindowVirtualizer<TItemElement>({
    ...options,
    enabled: isEnabled && scroll.isWindow,
  });
  const elementVirtualizer = useVirtualizer<HTMLElement, TItemElement>({
    ...options,
    getScrollElement: () => element,
    initialOffset: () => element?.scrollTop ?? 0,
    enabled: isEnabled && !scroll.isWindow && element !== null,
  });
  return scroll.isWindow ? windowVirtualizer : elementVirtualizer;
};

const savedScrollTops = new Map<string, number>();

/** Stops waiting for content to grow tall enough to restore an offset after this long. */
const RESTORE_TIMEOUT_MS = 2000;

/**
 * Keeps the scroller's offset per history entry, and puts it back on back or forward, as the window does by itself.
 * A mounted scroller keeps its position on new entries; a newly attached one starts at the top.
 */
const usePageScrollRestoration = (
  element: HTMLElement | null,
  scope: string,
) => {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const storageKey = `${scope}:${key}`;
  const stopRestoring = useRef<(() => void) | null>(null);
  // The entry a restore in flight belongs to, under the key a replace during it moves the entry to.
  const restoringKey = useRef(storageKey);
  // How the entry was last arrived at, which a replace as it loads leaves standing, and the scroller already restored for it.
  const arrival = useRef<{
    isPop: boolean;
    storageKey: string;
    element: HTMLElement | null;
    isReattached: boolean;
  }>({
    isPop: navigationType === "POP",
    storageKey,
    element: null,
    isReattached: false,
  });
  // The key the latest offset was saved under, which a replace moves on from the key the entry arrived with.
  const savedKey = useRef(storageKey);

  useEffect(() => {
    if (!element) return;
    const save = () => {
      // A restore's own writes clamp while the content is short, so they never stand in for the saved offset.
      if (stopRestoring.current) return;
      savedScrollTops.set(storageKey, element.scrollTop);
    };
    savedKey.current = storageKey;
    // An entry the viewer never scrolls still comes back where it was left.
    if (navigationType !== "POP") save();
    element.addEventListener("scroll", save, { passive: true });
    return () => element.removeEventListener("scroll", save);
  }, [element, storageKey, navigationType]);

  useLayoutEffect(() => () => stopRestoring.current?.(), []);

  // Before paint, so a page coming back never draws a frame at the top first.
  useLayoutEffect(() => {
    const isNewScroller = element !== arrival.current.element;
    if (
      navigationType !== "REPLACE" &&
      storageKey !== arrival.current.storageKey
    ) {
      arrival.current = {
        isPop: navigationType === "POP",
        storageKey,
        element: null,
        isReattached: false,
      };
    }
    // A page that replaces its entry as it loads, as for its search params, is still the one coming back.
    if (
      navigationType === "REPLACE" &&
      stopRestoring.current &&
      element === arrival.current.element
    ) {
      restoringKey.current = storageKey;
      return;
    }
    // A scroller that has gone or been replaced takes its restore with it.
    stopRestoring.current?.();
    // Within the same entry, a scroller that stops and starts scrolling the page comes back where it was left.
    if (!element) {
      if (arrival.current.element) {
        arrival.current.element = null;
        arrival.current.isReattached = true;
      }
      return;
    }
    // Restored once per scroller, so a replace that lands with the scroller itself still restores it.
    if (element === arrival.current.element) return;
    arrival.current.element = element;
    restoringKey.current = storageKey;
    const saved = arrival.current.isReattached
      ? savedScrollTops.get(savedKey.current)
      : arrival.current.isPop
        ? savedScrollTops.get(arrival.current.storageKey)
        : undefined;
    if (saved === undefined) {
      // A card can retain its offset while contained; a newly opened tab starts at the top.
      if (isNewScroller) element.scrollTop = 0;
      return;
    }
    const restore = () => {
      element.scrollTop = saved;
      if (element.scrollTop >= saved - 1) stop();
    };
    // The page's content may still be loading, so it tries again as the content grows, until the viewer scrolls.
    const observer = new ResizeObserver(restore);
    // Content that replaces a loading state is a new child, which the resize observer has to be told about.
    const children = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) observer.observe(node);
        }
      }
      restore();
    });
    // Content that never grows tall enough keeps the offset it was owed, not the one it clamped to.
    const timeout = window.setTimeout(() => stop(saved), RESTORE_TIMEOUT_MS);
    const stopOnInput = () => stop();
    const stop = (offset = element.scrollTop) => {
      observer.disconnect();
      children.disconnect();
      window.clearTimeout(timeout);
      element.removeEventListener("wheel", stopOnInput);
      element.removeEventListener("touchstart", stopOnInput);
      element.removeEventListener("pointerdown", stopOnInput);
      element.removeEventListener("keydown", stopOnInput);
      restoreInputStops.delete(element);
      stopRestoring.current = null;
      // Saved here as well as on scroll, since a replace during the restore moved the entry to a key no scroll has saved.
      savedScrollTops.set(restoringKey.current, offset);
    };
    // Stopped from outside, by leaving the page before the content grew, it still owes the saved offset.
    stopRestoring.current = () => stop(saved);
    // A pointer on the scrollbar takes over too.
    element.addEventListener("wheel", stopOnInput, { passive: true });
    element.addEventListener("touchstart", stopOnInput, { passive: true });
    element.addEventListener("pointerdown", stopOnInput);
    element.addEventListener("keydown", stopOnInput);
    restoreInputStops.set(element, stopOnInput);
    for (const child of element.children) observer.observe(child);
    children.observe(element, { childList: true });
    restore();
  }, [element, storageKey, navigationType]);
};
